import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

// shadcn/ui Badge — écrit à la main. Tokens sémantiques uniquement (P-06 / WCAG
// AA). IMPORTANT a11y (ST-NFR-03) : le badge porte TOUJOURS un libellé texte ;
// la couleur n'est qu'un renfort, jamais le seul porteur d'information.
//
// Variants STATUS compte (DS, Fase 3) — recette AA-SAFE OBLIGATOIRE : le TONE
// n'est JAMAIS la couleur du texte. Sur leur tint, les tones ne franchissent pas
// 4.5:1 en texte normal (active 3.0, suspended 3.1, invited 2.4 — finding a11y
// Fase 1). Donc : fond = tint (`bg-status-<X>-bg`), texte = `text-foreground`
// (contraste prouvé sur les tints), tone présent UNIQUEMENT via la bordure et,
// en option, le point (`withDot`). Le libellé texte reste l'unique porteur
// d'information (WCAG 1.4.1).
const badgeVariants = cva(
  'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
  {
    variants: {
      variant: {
        default: 'border-transparent bg-primary text-primary-foreground',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'border-border text-foreground',
        accent: 'border-transparent bg-accent text-accent-foreground',
        active: 'border-status-active/40 bg-status-active-bg text-foreground',
        suspended:
          'border-status-suspended/40 bg-status-suspended-bg text-foreground',
        invited: 'border-status-invited/40 bg-status-invited-bg text-foreground',
        inactive:
          'border-status-inactive/40 bg-status-inactive-bg text-foreground',
        // 5e tone status (DS sweep Cat 1) — même recette AA-SAFE, tone porté par
        // `--destructive` : fond = tint translucide (`bg-destructive/10`, contraste
        // foreground prouvé sur fond de page), texte = `text-foreground`, tone via
        // bordure + point. Employé pour l'état « Refusé » (et lien Expiré).
        refused: 'border-destructive/40 bg-destructive/10 text-foreground',
      },
    },
    defaultVariants: {
      variant: 'secondary',
    },
  },
);

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

// cva ne connaît pas la classe du point par variant : on la porte ici (status
// uniquement). Le point est un RENFORT visuel (aria-hidden) qui expose le tone
// sans jamais le poser sur le texte.
const STATUS_DOT_CLASS: Partial<Record<BadgeVariant, string>> = {
  active: 'bg-status-active',
  suspended: 'bg-status-suspended',
  invited: 'bg-status-invited',
  inactive: 'bg-status-inactive',
  refused: 'bg-destructive',
};

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {
  /** Préfixe un point coloré (aria-hidden) porté par le TONE du variant status.
   *  Le libellé texte reste l'unique porteur d'information (WCAG 1.4.1). */
  withDot?: boolean;
}

function Badge({
  className,
  variant,
  withDot = false,
  children,
  ...props
}: BadgeProps): React.JSX.Element {
  const dotClass = variant != null ? STATUS_DOT_CLASS[variant] : undefined;
  return (
    <span className={cn(badgeVariants({ variant }), className)} {...props}>
      {withDot && dotClass !== undefined ? (
        <span
          aria-hidden="true"
          className={cn('mr-1 h-1.5 w-1.5 rounded-full', dotClass)}
        />
      ) : null}
      {children}
    </span>
  );
}

export { Badge, badgeVariants };
